// src/components/ReferralLeaderboard.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Spinner,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";

/**
 * ReferralLeaderboard shows how many credited referrals each business has earned.
 * - Queries referrals table for credited referrals.
 * - Groups by referrer_id (integer).
 * - Joins back to businesses to show names.
 */
export default function ReferralLeaderboard({ businessId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchLeaderboard() {
      if (!businessId) return;

      // 1. Get all credited referrals for this business as referrer
      const { data: referrals, error: referralError } = await supabase
        .from("referrals")
        .select("referrer_id")
        .eq("referrer_id", businessId) // integer
        .eq("credited", true);

      if (referralError) {
        console.error("Referral query error:", referralError);
        setLoading(false);
        return;
      }

      if (!referrals || referrals.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // 2. Count credits per referrer_id
      const counts = {};
      referrals.forEach((r) => {
        counts[r.referrer_id] = (counts[r.referrer_id] || 0) + 1;
      });

      const referrerIds = Object.keys(counts).map(Number);

      // 3. Lookup business names
      const { data: businesses, error: businessError } = await supabase
        .from("businesses")
        .select("id, name")
        .in("id", referrerIds);

      if (businessError) {
        console.error("Business lookup error:", businessError);
        setLoading(false);
        return;
      }

      // 4. Build leaderboard
      const leaderboard = (businesses || [])
        .map((b) => ({
          name: b.name,
          credits: counts[b.id] || 0,
        }))
        .sort((a, b) => b.credits - a.credits);

      setRows(leaderboard);
      setLoading(false);
    }

    fetchLeaderboard();
  }, [businessId]);

  return (
    <Box>
      <Text fontSize="lg" fontWeight="bold" mb={2}>
        🏆 Referral Leaderboard
      </Text>
      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No credited referrals yet.
        </Text>
      ) : (
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>Business</Th>
              <Th isNumeric>Credits</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, i) => (
              <Tr key={i}>
                <Td>{row.name}</Td>
                <Td isNumeric>
                  <Badge colorScheme="green">{row.credits}</Badge>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}
